import React from "react";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaGlobe, FaUniversity } from "react-icons/fa";

const ProfessionalModern = ({ data }) => {
    const {
        to,
        clientAddress,
        clientPhone,
        clientEmail,
        title,
        date,
        dueDate,
        invoiceItems,
        subTotal,
        taxRate,
        taxAmount,
        discount,
        totalAmount,
        notes,
        terms,
        companyDetails
    } = data;

    return (
        <div className="w-full bg-white p-12 font-sans text-slate-800 leading-relaxed shadow-sm min-h-[1100px] flex flex-col justify-between">
            <div>
                {/* Header */}
                <div className="flex justify-between items-start border-b-4 border-indigo-600 pb-10 mb-12">
                    <div className="space-y-4">
                        {companyDetails?.logo ? (
                            <img src={companyDetails.logo} alt="Logo" className="h-16 w-auto object-contain" />
                        ) : (
                            <div className="text-4xl font-black tracking-tighter text-indigo-600 uppercase italic">
                                {companyDetails?.name?.split(' ')[0] || "INVOICE"}
                            </div>
                        )}
                        <h1 className="text-5xl font-black uppercase tracking-tighter text-slate-900 mt-4">INVOICE</h1>
                    </div>

                    <div className="text-right space-y-2">
                        <div className="bg-indigo-600 text-white px-6 py-2 rounded-full inline-block font-black text-sm uppercase tracking-widest mb-4">
                            # {title || "INV-001"}
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                            <div>Date Issued:</div>
                            <div className="text-slate-900">{date}</div>
                            <div>Due Date:</div>
                            <div className="text-rose-500">{dueDate}</div>
                        </div>
                    </div>
                </div>

                {/* Addresses */}
                <div className="grid grid-cols-2 gap-20 mb-12">
                    <div className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Company Details</h4>
                        <div className="space-y-1">
                            <div className="text-lg font-black text-slate-900">{companyDetails?.name}</div>
                            <div className="text-sm font-medium text-slate-500 max-w-[250px]">{companyDetails?.address}</div>
                            <div className="flex flex-col pt-3 space-y-1 text-xs font-bold text-slate-400">
                                <span className="flex items-center gap-2"><FaPhone className="text-indigo-600" /> {companyDetails?.phone}</span>
                                <span className="flex items-center gap-2"><FaEnvelope className="text-indigo-600" /> {companyDetails?.email}</span>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Bill To</h4>
                        <div className="space-y-1">
                            <div className="text-lg font-black text-slate-900">{to}</div>
                            <div className="text-sm font-medium text-slate-500 max-w-[250px]">{clientAddress}</div>
                            <div className="flex flex-col pt-3 space-y-1 text-xs font-bold text-slate-400">
                                {clientPhone && <span className="flex items-center gap-2"><FaPhone className="text-indigo-600" /> {clientPhone}</span>}
                                {clientEmail && <span className="flex items-center gap-2"><FaEnvelope className="text-indigo-600" /> {clientEmail}</span>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="mb-12">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-900 text-white font-black uppercase text-[10px] tracking-widest">
                                <th className="p-5 rounded-l-2xl">Description</th>
                                <th className="p-5 text-center">Qty</th>
                                <th className="p-5 text-right">Unit Price</th>
                                <th className="p-5 text-right rounded-r-2xl">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {(invoiceItems || []).map((item, idx) => (
                                <tr key={idx} className="group hover:bg-slate-50 transition-colors">
                                    <td className="p-6">
                                        <div className="font-bold text-slate-800">{item.item}</div>
                                        <div className="text-xs text-slate-400 font-medium italic mt-1">{item.description || "No additional description"}</div>
                                    </td>
                                    <td className="p-6 text-center font-black text-slate-600">{item.qty}</td>
                                    <td className="p-6 text-right font-black text-slate-600">Nu. {parseFloat(item.unitPrice).toLocaleString()}</td>
                                    <td className="p-6 text-right font-black text-slate-900 leading-none">
                                        Nu. {parseFloat(item.amount).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Totals & Notes Row */}
                <div className="grid grid-cols-2 gap-12">
                    <div className="space-y-6">
                        <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 italic text-slate-500 text-sm leading-relaxed">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 not-italic mb-2">Important Notes</h4>
                            {notes || "Thank you for your business. We appreciate your prompt payment."}
                        </div>
                        {terms && (
                            <div className="px-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                Terms: {terms}
                            </div>
                        )}
                    </div>

                    <div className="bg-slate-900 text-white p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl animate-pulse"></div>
                        <div className="space-y-4 relative z-10">
                            <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-widest">
                                <span>Subtotal</span>
                                <span className="text-white">Nu. {parseFloat(subTotal).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-widest">
                                <span>Tax ({taxRate}%)</span>
                                <span className="text-white">Nu. {parseFloat(taxAmount).toLocaleString()}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-xs font-bold text-rose-400 uppercase tracking-widest">
                                    <span>Discount</span>
                                    <span>- Nu. {parseFloat(discount).toLocaleString()}</span>
                                </div>
                            )}
                            <div className="pt-6 mt-6 border-t border-white/10 flex justify-between items-end">
                                <div className="space-y-1">
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400">Total Payable</span>
                                    <div className="text-4xl font-black tracking-tighter">Nu. {parseFloat(totalAmount).toLocaleString()}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="mt-20 border-t border-slate-100 pt-10 flex justify-between items-end">
                <div className="space-y-4">
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Banking Details</h4>
                    <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                        <FaUniversity className="text-indigo-600 mt-1" />
                        <div className="text-xs space-y-1 font-bold">
                            <div className="text-slate-900 group-hover:text-indigo-600 transition-colors">{companyDetails?.bankName || "Digital Kidu Bank"}</div>
                            <div className="text-slate-500">ACC: {companyDetails?.accountNo || "122656705"}</div>
                        </div>
                    </div>
                </div>

                <div className="text-right space-y-6">
                    <div className="w-48 h-px bg-slate-200 ml-auto"></div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 italic">Authorized Signature</div>
                </div>
            </div>
        </div>
    );
};

export default ProfessionalModern;
